/**
 * MyAgent API Service Factory
 * Platform-agnostic API wrappers for MyAgent RPC methods
 */

import { isAuthError } from 'workers-common'
import { resolveProviderAndKey } from './llm-config.js'

function resolveLlmConfig(botLlm, globalConfig) {
  const isLocal = botLlm?.mode === 'local' || globalConfig?.executionMode === 'local'
  if (!isLocal) return { mode: 'remote' }
  if (botLlm?.provider && (botLlm.apiKey || botLlm.provider === 'ollama')) {
    return { mode: 'local', provider: botLlm.provider, apiKey: botLlm.apiKey, model: botLlm.model }
  }
  return { mode: 'local', ...resolveProviderAndKey(globalConfig) }
}

/**
 * Create MyAgent API service
 * @param {Object} options
 * @param {string} options.workerName - Worker name for callByName
 * @param {Function} options.getClient - Function that returns client instance
 * @param {Function} [options.onAuthError] - Callback for authentication errors
 * @param {Function} [options.getSessionTarget] - Returns 'local' or worker name for session routing
 * @param {Object} [options.llmConfig] - LLM config service
 * @param {Function} [options.getFrontendLlmSdk] - Function to get frontend LLM SDK (mobile direct mode)
 * @param {string} [options.platform] - 'web' | 'mobile'
 * @returns {Object} API methods
 */
export function createApiService({ workerName, getClient, onAuthError, getSessionTarget, llmConfig, getFrontendLlmSdk, platform = 'web' }) {
  const callWithAuth = async (target, method, params, opts) => {
    const client = getClient()
    try {
      return await client.callByNameWithAuth(target, method, params, opts)
    } catch (error) {
      if (isAuthError(error) && onAuthError) {
        console.warn('[API] Authentication error detected:', error.message)
        onAuthError(error)
      }
      throw error
    }
  }

  const callWorker = (method, params, opts) => callWithAuth(workerName, method, params, opts)

  const callSession = async (method, params) => {
    const target = getSessionTarget ? await getSessionTarget() : workerName
    return callWithAuth(target, method, params)
  }

  const call = async (method, params) => {
    const client = getClient()
    return client.callByName(workerName, method, params)
  }

  return {
    // System APIs
    healthCheck: () => call('system.health', []),
    getAvailableTools: () => call('system.getAvailableTools', []),
    getAvailableModels: () => call('system.getAvailableModels', []),

    // --- Session APIs (unified, per-conversation) ---

    getConversationIndex: () => callSession('session.getConversationIndex', [{}]),

    createConversation: (params) => callSession('session.createConversation', [params]),

    renameConversation: (conversationId, title) => callSession('session.renameConversation', [{ conversationId, title }]),

    deleteConversation: (conversationId) => callSession('session.deleteConversation', [{ conversationId }]),

    getMessages: (conversationId, maxMessages) =>
      callSession('session.getMessages', [{ conversationId, maxMessages }]),

    getMessagesForLLM: (conversationId, nameMap) =>
      callSession('session.getMessagesForLLM', [{ conversationId, nameMap }]),

    saveMessages: (conversationId, messages) =>
      callSession('session.saveMessages', [{ conversationId, messages }]),

    getParticipants: (conversationId) =>
      callSession('session.getParticipants', [{ conversationId }]),

    setParticipants: (conversationId, participantRoleIds) =>
      callSession('session.setParticipants', [{ conversationId, participantRoleIds }]),

    clearSession: () => callSession('session.clear', [{}]),

    // --- Chat ---

    // Single-step LLM call (no tool execution loop). Used by tool agent.
    chatSingleStep: async ({ messages, tools, botConfig: botCfg }) => {
      const globalConfig = llmConfig ? await llmConfig.getConfig() : null
      const { mode, provider, apiKey, model } = resolveLlmConfig(botCfg?.llm, globalConfig)

      // Remote mode: use worker's own API keys via chat.chatStep
      if (mode === 'remote') return callWorker('chat.chatStep', [{ messages, tools, botConfig: botCfg }])

      if (platform === 'mobile' && getFrontendLlmSdk) {
        return getFrontendLlmSdk().chatSingleStep({ provider, apiKey, model, messages, tools })
      }

      return callWorker('llm.proxy', [{ provider, apiKey, model, messages, tools }])
    },

    chat: async (params, botConfig = null) => {
      const globalConfig = llmConfig ? await llmConfig.getConfig() : null
      const { mode, provider, apiKey, model } = resolveLlmConfig(botConfig?.llm, globalConfig)

      if (mode === 'remote') return callWorker('chat.chatStep', [{ ...params, botConfig }])

      if (!provider || (!apiKey && provider !== 'ollama')) {
        throw new Error('No API key configured. Please configure your API key in settings.')
      }

      if (platform === 'mobile' && getFrontendLlmSdk) {
        return getFrontendLlmSdk().chatSingleStep({ provider, apiKey, model, messages: params.messages })
      }

      return callWorker('llm.proxy', [{
        provider, apiKey, model, messages: params.messages,
      }])
    },
  }
}
